import React, { useState, useEffect } from "react";
import { MapPin, Navigation, Search, Loader2, Home } from "lucide-react";
import {
  CardContent,
  CardFooter,
} from "./ui/card";
import { Card } from "flowbite-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { getCurrentLocation } from "@/lib/locationService";
import { useTranslation } from 'react-i18next';
import { quietLog } from '../lib/prayerTimesAPI';

interface LocationSelectorProps {
  currentLocation?: string;
  onLocationChange?: (location: string) => void;
  onAutoDetectChange?: (enabled: boolean) => void;
  autoDetectEnabled?: boolean;
}

// Add mosque data structure
interface Mosque {
  name: string;
  address: string;
  city: string;
}

const LocationSelector = ({
  currentLocation = "Stockholm, Sverige",
  onLocationChange = () => {},
  onAutoDetectChange = () => {},
  autoDetectEnabled = true,
}: LocationSelectorProps) => {
  const { t, i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  // Add state for mosque search
  const [showMosqueSearch, setShowMosqueSearch] = useState(false);
  
  // Define mosque data
  const mosques: Mosque[] = [
    // Gävle
    { name: "Gävle moské", address: "Södra Kungsgatan 31, 802 52 Gävle", city: "Gävle" },
    
    // Göteborg
    { name: "Nasirmoskén", address: "Tolvskillingsgatan 1, 414 82 Göteborg", city: "Göteborg" },
    { name: "Bellevue moské", address: "Generalsgatan 2A, 415 05 Göteborg", city: "Göteborg" },
    
    // Karlskrona
    { name: "Kungsmarkens moské", address: "Kaptensgatan 2, 371 38 Karlskrona", city: "Karlskrona" },
    
    // Malmö
    { name: "Malmö moské", address: "Ramels väg 121, 214 47 Malmö", city: "Malmö" },
    { name: "Mahmoodmoskén", address: "Ögårdsvägen 2, 212 31 Malmö", city: "Malmö" },
    
    // Stockholm
    { name: "Stockholms moské (Zayed bin Sultan Al Nahyan-moskén)", address: "Kapellgränd 10, 116 25 Stockholm", city: "Stockholm" },
    { name: "Bengaliska Moskén", address: "Kocksgatan 23B, 116 24 Stockholm", city: "Stockholm" },
    { name: "Brandbergenmoskén", address: "Brandbergens centrum, 136 75 Brandbergen", city: "Stockholm" },
    { name: "Fittja moské", address: "Fittjavägen 19, 145 52 Norsborg", city: "Stockholm" },
    { name: "Husby moské", address: "Bergengatan 1, 164 35 Kista", city: "Stockholm" },
    { name: "Järfälla moské", address: "Drabantvägen 2, 177 50 Järfälla", city: "Stockholm" },
    { name: "Imam Ali Islamic Center", address: "Skälbyvägen 2, 175 67 Järfälla", city: "Stockholm" },
    { name: "Khadija Center", address: "Kista Torg 5, 164 42 Kista", city: "Stockholm" },
    
    // Trollhättan
    { name: "Trollhättans moské", address: "Kaptensgatan 15, 461 52 Trollhättan", city: "Trollhättan" },
    
    // Umeå
    { name: "Umeå moské", address: "Skolgatan 81, 903 30 Umeå", city: "Umeå" },
    
    // Uppsala
    { name: "Uppsala moské", address: "Vattholmavägen 2, 754 40 Uppsala", city: "Uppsala" },
    
    // Växjö
    { name: "Araby moské", address: "Hjalmar Petris väg 31, 352 46 Växjö", city: "Växjö" },
  ];
  
  // Add state for mosque search results
  const [mosqueResults, setMosqueResults] = useState<Mosque[]>([]);
  const [mosqueSearchQuery, setMosqueSearchQuery] = useState("");
  const [isSearchingMosque, setIsSearchingMosque] = useState(false);
  
  // Define city names in Swedish
  const cityNames = [
    "Stockholm, Sverige",
    "Göteborg, Sverige",
    "Malmö, Sverige",
    "Uppsala, Sverige",
    "Västerås, Sverige",
    "Örebro, Sverige",
    "Linköping, Sverige",
    "Helsingborg, Sverige",
    "Jönköping, Sverige",
    "Norrköping, Sverige",
  ];
  
  // Translate city names based on current language
  const getTranslatedCities = () => {
    return cityNames.map(city => ({
      original: city,
      translated: t(city)
    }));
  };
  
  const [searchResults, setSearchResults] = useState(getTranslatedCities());

  // Update search results when language changes
  useEffect(() => {
    setSearchResults(getTranslatedCities());
  }, [i18n.language]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    // Filter locations based on search query
    const filteredResults = getTranslatedCities().filter((location) =>
      location.translated.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    // Simulate API call delay
    setTimeout(() => {
      setSearchResults(filteredResults.length ? filteredResults : getTranslatedCities());
      setIsSearching(false);
    }, 500);
  };

  const [isDetecting, setIsDetecting] = useState(false);
  
  // Cache för senaste position
  const [lastPosition, setLastPosition] = useState<{
    timestamp: number;
    data: any;
  } | null>(null);

  const handleAutoDetect = async () => {
    setIsDetecting(true);
    try {
      // Kontrollera om vi har en cachad position som är mindre än 10 minuter gammal
      const now = Date.now();
      if (lastPosition && now - lastPosition.timestamp < 10 * 60 * 1000) {
        console.log("Använder cachad position");
        onLocationChange(lastPosition.data.fullLocation);
        setIsDetecting(false);
        return;
      }
      
      // Hämta användarens position
      const locationData = await getCurrentLocation();
      
      if (locationData) {
        // Cachelagra positionen
        setLastPosition({
          timestamp: now,
          data: locationData
        });
        
        // Uppdatera platsen med den detekterade staden
        onLocationChange(locationData.fullLocation);
      } else {
        // Fallback till Stockholm om platsdetektering misslyckas
        onLocationChange("Stockholm, Sverige");
      }
    } catch (error) {
      quietLog("Fel vid automatisk platsdetektering", error);
      onLocationChange("Stockholm, Sverige");
    } finally {
      setIsDetecting(false);
    }
  };

  const handleLocationSelect = (location: { original: string, translated: string }) => {
    onLocationChange(location.original); // Always use the original name for API calls
    setSearchQuery("");
  };

  const handleAutoDetectToggle = (checked: boolean) => {
    onAutoDetectChange(checked);
    if (checked) {
      handleAutoDetect();
    }
  };

  // Add function to handle mosque search
  const handleMosqueSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!mosqueSearchQuery.trim()) {
      // Show all mosques if search query is empty
      setMosqueResults(mosques);
      return;
    }

    setIsSearchingMosque(true);
    
    // Filter mosques based on search query
    const filteredMosques = mosques.filter((mosque) =>
      mosque.name.toLowerCase().includes(mosqueSearchQuery.toLowerCase()) ||
      mosque.city.toLowerCase().includes(mosqueSearchQuery.toLowerCase()) ||
      mosque.address.toLowerCase().includes(mosqueSearchQuery.toLowerCase())
    );
    
    // Simulate API call delay
    setTimeout(() => {
      setMosqueResults(filteredMosques);
      setIsSearchingMosque(false);
    }, 500);
  };

  // Initialize mosque results with all mosques
  useEffect(() => {
    setMosqueResults(mosques);
  }, []);

  // Add function to handle mosque selection
  const handleMosqueSelect = (mosque: Mosque) => {
    onLocationChange(`${mosque.city}, Sverige`);
    setMosqueSearchQuery("");
    setShowMosqueSearch(false);
  };

  return (
    <Card className="w-full max-w-md mx-auto overflow-hidden animate-fadeIn shadow-lg">
      <div className="bg-purple-600 text-white p-4 rounded-t-lg">
        <div className="flex items-center justify-center gap-2 mb-2">
          <MapPin className="h-5 w-5 text-purple-200" />
          <h2 className="text-xl font-bold">{t('location')}</h2>
        </div>
      </div>
      
      <div className="p-4 bg-gradient-to-b from-white to-purple-50 dark:from-gray-800 dark:to-purple-950/30 space-y-4">
        {/* Current Location */}
        <div className="flex items-center gap-2 mb-4">
          <MapPin className="text-purple-600 dark:text-purple-400" size={18} />
          <span className="font-medium">{t(currentLocation) || t('location')}</span>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300"
            onClick={handleAutoDetect}
            disabled={isDetecting}
          >
            {isDetecting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Navigation className="h-4 w-4" />
            )}
          </Button>
        </div>

        {/* Auto-detect toggle */}
        <div className="flex items-center mb-4">
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={autoDetectEnabled}
              onChange={() => handleAutoDetectToggle(!autoDetectEnabled)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 dark:peer-focus:ring-purple-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-purple-600"></div>
            <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
              {t('useMyLocationAutomatically')}
            </span>
          </label>
        </div>
        
        {/* Location Search */}
        <form onSubmit={handleSearch} className="relative">
          <div className="relative">
            <input
              type="text"
              placeholder={t('searchForLocation')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full p-2.5 pl-10 text-sm text-gray-900 bg-white border border-purple-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:border-purple-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-purple-500 dark:focus:border-purple-500"
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <MapPin className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </div>
            <button
              type="submit"
              className="absolute right-2.5 bottom-2.5 text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300"
              disabled={isSearching}
            >
              {isSearching ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
            </button>
          </div>
        </form>

        {/* Search Results */}
        {searchQuery && (
          <div className="mt-2 border rounded-md divide-y max-h-48 overflow-y-auto">
            {isSearching ? (
              <div className="p-3 text-center text-sm text-gray-500">
                {t('searching')}
              </div>
            ) : searchResults.length === 0 ? (
              <div className="p-3 text-center text-sm text-gray-500">
                {t('noResultsFound')}
              </div>
            ) : (
              searchResults.map((location, index) => (
                <button
                  key={index}
                  className="w-full text-left p-2 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
                  onClick={() => handleLocationSelect(location)}
                >
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 text-gray-400 mr-2" />
                    {location.translated}
                  </div>
                </button>
              ))
            )}
          </div>
        )}

        {/* Mosque Search */}
        <form onSubmit={handleMosqueSearch} className="relative mt-4">
          <div className="relative">
            <input
              type="text"
              placeholder={t('searchForMosque')}
              value={mosqueSearchQuery}
              onChange={(e) => {
                setMosqueSearchQuery(e.target.value);
                if (e.target.value.trim()) {
                  const filtered = mosques.filter((mosque) =>
                    mosque.name.toLowerCase().includes(e.target.value.toLowerCase()) ||
                    mosque.city.toLowerCase().includes(e.target.value.toLowerCase()) ||
                    mosque.address.toLowerCase().includes(e.target.value.toLowerCase())
                  );
                  setMosqueResults(filtered);
                } else {
                  setMosqueResults(mosques);
                }
              }}
              className="w-full p-2.5 pl-10 text-sm text-gray-900 bg-white border border-purple-300 rounded-lg focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:border-purple-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-purple-500 dark:focus:border-purple-500"
            />
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <Home className="w-5 h-5 text-gray-500 dark:text-gray-400" />
            </div>
            <button
              type="submit"
              className="absolute right-2.5 bottom-2.5 text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300"
              disabled={isSearchingMosque}
            >
              {isSearchingMosque ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Search className="w-4 h-4" />
              )}
            </button>
          </div>
        </form>

        {/* Mosque Search Results */}
        {mosqueSearchQuery.trim() !== "" && (
          <div className="mt-2 border rounded-md divide-y max-h-48 overflow-y-auto">
            {isSearchingMosque ? (
              <div className="p-3 text-center text-sm text-gray-500">
                {t("searchingMosques")}
              </div>
            ) : mosqueResults.length === 0 ? (
              <div className="p-3 text-center text-sm text-gray-500">
                {t("noMosquesFound")}
              </div>
            ) : (
              mosqueResults.map((mosque, index) => (
                <div key={index} 
                  className="w-full p-2 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                  onClick={() => {
                    // Öppna Google Maps i ny flik
                    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      `${mosque.name} ${mosque.address}`
                    )}`;
                    window.open(mapsUrl, '_blank');
                  }}
                  onContextMenu={(e) => {
                    // Förhindra kontextmeny
                    e.preventDefault();
                    // Välj moskéns plats för bönetider
                    handleMosqueSelect(mosque);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 text-left">
                      <div className="flex items-center">
                        <Home className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <div className="font-medium">{mosque.name}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{mosque.address}</div>
                        </div>
                      </div>
                    </div>
                    <div 
                      className="ml-2 flex items-center"
                      onClick={(e) => e.stopPropagation()} // Stoppa event bubbling
                    >
                      <button
                        className="text-purple-600 hover:text-purple-800 dark:text-purple-400 dark:hover:text-purple-300 bg-white dark:bg-gray-700 rounded-md p-2 shadow-sm border border-purple-200 dark:border-purple-800 flex items-center justify-center"
                        onClick={(e) => {
                          // Öppna Google Maps i ny flik
                          const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                            `${mosque.name} ${mosque.address}`
                          )}`;
                          window.open(mapsUrl, '_blank');
                        }}
                        onContextMenu={(e) => {
                          // Förhindra kontextmeny
                          e.preventDefault();
                          // Välj moskéns plats för bönetider
                          handleMosqueSelect(mosque);
                        }}
                        title={t('openInMaps')}
                        aria-label={t('openInMaps')}
                      >
                        <MapPin className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
        
        <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
          {t('locationDataUsage')}
        </div>
      </div>
    </Card>
  );
};

export default LocationSelector;